import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import * as Sentry from '@sentry/react';
import * as ph from '../services/posthogClient';
import { getSession, login as apiLogin, logout as apiLogout, signUp as apiSignUp } from '../api/client';

/**
 * Auth Context
 *
 * Provides authentication state and methods.
 * Handles session persistence and OAuth callbacks.
 */

const AuthContext = createContext(undefined);
const NON_ACTIONABLE_OAUTH_ERRORS = new Set([
  'google_init_failed',
  'google_denied',
  'google_callback_failed',
  'google_token_failed',
  'missing_state',
  'invalid_state',
  'missing_code',
]);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Read auth error from URL params before any effects can clear them
  const [initialAuthError] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('auth_error');
  });

  // Check for existing session on mount
  useEffect(() => {
    checkSession();

    // Handle OAuth success/error from URL params
    const params = new URLSearchParams(window.location.search);
    if (params.get('auth_success')) {
      // Reload session after OAuth success
      checkSession();
      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname);
    } else if (initialAuthError) {
      setError(initialAuthError);
      window.history.replaceState({}, '', window.location.pathname);
      if (!NON_ACTIONABLE_OAUTH_ERRORS.has(initialAuthError)) {
        // Report only actionable OAuth callback errors to Sentry — error code only, no PII.
        Sentry.captureMessage(`OAuth callback error: ${initialAuthError}`, {
          level: 'warning',
          tags: { auth_error: initialAuthError },
        });
      }
    }
  }, []);

  const checkSession = useCallback(async () => {
    try {
      setIsLoading(true);
      const data = await getSession();
      const authedUser = data.authenticated ? data.user : null;
      setUser(authedUser);
      Sentry.setUser(authedUser ? { id: authedUser.id, email: authedUser.email } : null);
      if (authedUser) {
        ph.identify(authedUser.id, { email: authedUser.email, created_at: authedUser.createdAt });
      }
    } catch (err) {
      console.error('Session check failed:', err);
      setUser(null);
      Sentry.setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const login = useCallback(async (email, password) => {
    try {
      setError(null);
      const data = await apiLogin(email, password);
      setUser(data.user);
      Sentry.setUser(data.user ? { id: data.user.id, email: data.user.email } : null);
      if (data.user) {
        ph.identify(data.user.id, { email: data.user.email, created_at: data.user.createdAt });
      }
      return data;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  const signUp = useCallback(async (email, password, name) => {
    try {
      setError(null);
      const data = await apiSignUp(email, password, name);
      setUser(data.user);
      Sentry.setUser(data.user ? { id: data.user.id, email: data.user.email } : null);
      if (data.user) {
        ph.identify(data.user.id, { email: data.user.email, created_at: data.user.createdAt });
      }
      return data;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiLogout();
      setUser(null);
      setError(null);
      Sentry.setUser(null);
      ph.reset();
    } catch (err) {
      console.error('Logout error:', err);
      // Clear local state even if API fails
      setUser(null);
      Sentry.setUser(null);
      ph.reset();
    }
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const value = {
    user,
    isLoading,
    isAuthenticated: !!user,
    error,
    login,
    signUp,
    logout,
    checkSession,
    clearError,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

/**
 * Hook to access auth context
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
